import SearchIcon from '@mui/icons-material/Search';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { authReducer } from '@bitstopco/bitstop-theme';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

import { TEXTAREA_CONFIG } from '@/forms/schemas';
import { getOperatorId } from '@/helpers/jwt';
import { useAppSelector } from '@/store';
import { useCreateLocationNoteMutation } from '@/store/api/services/kyc/notes';
import {
  useAssignMachineToLocationMutation,
  useGetLocationsQuery,
  useRemoveMachineFromLocationMutation,
} from '@/store/api/services/locations/locations';

import type { MachineDetail } from '@/types/machine';

import {
  buildAssignPayload,
  defaultRelocationNoteText,
  defaultRemovalNoteText,
  errorMessage,
  formatLocationAddress,
  parseCurrentLocationId,
  shouldUninstallBeforeAssign,
} from './changeMachineLocation.helpers';

const {
  selectors: { userInfo },
} = authReducer;

type Stage = 'pick' | 'confirm-move' | 'confirm-remove';

const PAGE_SIZE = 25;

const ChangeMachineLocation = ({ hide, props, setTitle }: ModalChildrenProps) => {
  const machine = props?.data as MachineDetail | undefined;
  const atmId = machine?.id;
  const currentLocationId = parseCurrentLocationId(machine?.location?.id);
  const hasCurrentLocation = currentLocationId !== undefined;
  const currentLocationName = machine?.location?.name || machine?.location?.full_address;

  const userData = useAppSelector(userInfo);
  const authorName = [userData?.first_name, userData?.last_name].filter(Boolean).join(' ');

  const [stage, setStage] = useState<Stage>('pick');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  // Pre-filled when entering a confirm stage that leaves the current location (see
  // goToConfirmRemove/goToConfirmMove); editable so the operator can add context beyond the
  // auto-generated summary. Documents "who removed/relocated this machine and why" without a
  // separate trip to Location single's notes tab.
  const [noteText, setNoteText] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  // Set only if uninstall succeeded but the follow-up assign then failed -- the machine is
  // genuinely uninstalled at this point, so this must stay visible until the user retries or
  // acknowledges it, never silently dismissed.
  const [strandedAfterUninstall, setStrandedAfterUninstall] = useState(false);

  useEffect(() => {
    setTitle(hasCurrentLocation ? 'Change Location' : 'Install at Location');
  }, [hasCurrentLocation, setTitle]);

  const runSearch = () => {
    setSearchTerm(searchInput);
    setPageSize(PAGE_SIZE);
  };

  const {
    data: {
      data: locations = [],
      meta: { pagination: locationsPagination } = {},
    } = {},
    isFetching: isSearching,
    isError: isSearchError,
  } = useGetLocationsQuery({
    search_text: searchTerm || undefined,
    pagination: {
      current_page: 1,
      items_per_page: pageSize,
      sort_ascending: true,
      sort_by: 'location_id',
    },
  });

  const hasMoreLocations = (locationsPagination?.total_items ?? 0) > locations.length;

  // Atlas rejects teller-type locations for assign-atms (409), and the machine is already at
  // its own current location -- both are filtered out here rather than surfaced as a raw error.
  const selectableLocations = useMemo(
    () => locations.filter((l) => l.type !== 'teller' && l.location_id !== currentLocationId),
    [locations, currentLocationId],
  );

  const selectedLocation = useMemo(
    () => selectableLocations.find((l) => l.location_id === selectedLocationId) ?? null,
    [selectableLocations, selectedLocationId],
  );

  const [assignAtm, { isLoading: isAssigning }] = useAssignMachineToLocationMutation();
  const [removeAtm, { isLoading: isRemoving }] = useRemoveMachineFromLocationMutation();
  const [createNote] = useCreateLocationNoteMutation();

  // Documents "who removed/relocated this machine" on the location being left, the same note
  // system Location single's notes tab reads from. Fire-and-report rather than blocking: the
  // uninstall/relocate already committed by the time this runs, so a note failure shouldn't look
  // like the whole action failed -- it gets its own toast instead.
  const saveLocationNote = async (locationId: number, text: string) => {
    if (!text.trim()) return;
    try {
      await createNote({
        location_id: locationId,
        operator_id: getOperatorId(),
        note_text: text.trim(),
        author: authorName,
      }).unwrap();
    } catch (error) {
      toast.error(`Machine location updated, but the note failed to save: ${errorMessage(error)}`);
    }
  };

  const goToConfirmMove = () => {
    if (hasCurrentLocation && atmId && selectedLocation) {
      setNoteText(defaultRelocationNoteText(atmId, selectedLocation.location_name));
    }
    setStage('confirm-move');
  };

  const goToConfirmRemove = () => {
    if (atmId) setNoteText(defaultRemovalNoteText(atmId));
    setStage('confirm-remove');
  };

  const handleConfirmMove = async () => {
    if (!atmId || !selectedLocation) return;

    try {
      // Relocation only runs the assign call if a preceding uninstall (when the machine has a
      // current location) succeeds -- an atomic backend endpoint was considered and deferred;
      // see DEV-2195. If uninstall fails, nothing has changed and the user can just retry.
      if (
        shouldUninstallBeforeAssign(hasCurrentLocation, strandedAfterUninstall) &&
        currentLocationId
      ) {
        await removeAtm({ location_id: currentLocationId, atm_id: atmId }).unwrap();
        await saveLocationNote(currentLocationId, noteText);
      }
    } catch (error) {
      toast.error(`Failed to remove machine from its current location: ${errorMessage(error)}`);
      return;
    }

    try {
      await assignAtm(buildAssignPayload(atmId, selectedLocation.location_id)).unwrap();
      setStrandedAfterUninstall(false);
      toast.success(
        hasCurrentLocation
          ? `Machine relocated to ${selectedLocation.location_name}.`
          : `Machine installed at ${selectedLocation.location_name}.`,
      );
      hide();
    } catch (error) {
      // The uninstall (if any) already committed -- the machine is uninstalled right now. This
      // must stay on screen, not a toast that can go unnoticed, since Ops is acting on-site.
      if (hasCurrentLocation) setStrandedAfterUninstall(true);
      toast.error(`Failed to assign machine to the new location: ${errorMessage(error)}`);
    }
  };

  const handleConfirmRemove = async () => {
    if (!atmId || !currentLocationId) return;
    try {
      await removeAtm({ location_id: currentLocationId, atm_id: atmId }).unwrap();
      await saveLocationNote(currentLocationId, noteText);
      toast.success('Machine removed from its location.');
      hide();
    } catch (error) {
      toast.error(`Failed to remove machine from its location: ${errorMessage(error)}`);
    }
  };

  const isBusy = isAssigning || isRemoving;

  return (
    <>
      <DialogContent sx={{ py: 2 }}>
        {strandedAfterUninstall && (
          <Alert severity="error" sx={{ mb: 2 }} data-test="machine-location-stranded-alert">
            <AlertTitle>Machine is now uninstalled</AlertTitle>
            <Typography variant="body2">
              The machine was removed from its previous location, but assigning it to{' '}
              <b>{selectedLocation?.location_name}</b> failed. It currently has no location. Pick a
              location and confirm again to retry.
            </Typography>
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Current location:{' '}
          {hasCurrentLocation ? (
            <b>{currentLocationName ?? `Location ${currentLocationId}`}</b>
          ) : (
            <i>None (uninstalled)</i>
          )}
        </Typography>

        {stage === 'confirm-remove' ? (
          <>
            <Typography variant="h6">
              Remove this machine from <b>{currentLocationName}</b>?
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              The machine will be left uninstalled until it's assigned to a new location.
            </Alert>
            <TextField
              fullWidth
              {...TEXTAREA_CONFIG}
              sx={{ ...TEXTAREA_CONFIG.sx, mt: 2 }}
              label="Note (optional)"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              inputProps={{ 'data-test': 'machine-location-note-input' }}
            />
          </>
        ) : stage === 'confirm-move' ? (
          <>
            <Typography variant="h6">
              {hasCurrentLocation ? (
                <>
                  Move this machine from <b>{currentLocationName}</b> to{' '}
                  <b>{selectedLocation?.location_name}</b>?
                </>
              ) : (
                <>
                  Install this machine at <b>{selectedLocation?.location_name}</b>?
                </>
              )}
            </Typography>
            {selectedLocation && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {[`ID ${selectedLocation.location_id}`, formatLocationAddress(selectedLocation)]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            )}
            {hasCurrentLocation && (
              <TextField
                fullWidth
                {...TEXTAREA_CONFIG}
                sx={{ ...TEXTAREA_CONFIG.sx, mt: 2 }}
                label={`Note for ${currentLocationName} (optional)`}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                inputProps={{ 'data-test': 'machine-location-note-input' }}
              />
            )}
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Search for a location
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by location name, address, or ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') runSearch();
              }}
              inputProps={{ 'data-test': 'machine-location-search-input' }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={runSearch} data-test="machine-location-search-button">
                    <SearchIcon />
                  </IconButton>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Box
              sx={{
                maxHeight: 320,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
              data-test="machine-location-picker-results"
            >
              {isSearchError ? (
                <Typography variant="body2" color="error" sx={{ p: 2 }}>
                  Failed to load locations. Try searching again.
                </Typography>
              ) : isSearching ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Searching...
                </Typography>
              ) : selectableLocations.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No locations found.
                </Typography>
              ) : (
                <>
                  <List disablePadding>
                    {selectableLocations.map((location) => (
                      <ListItemButton
                        key={location.location_id}
                        selected={location.location_id === selectedLocationId}
                        onClick={() => setSelectedLocationId(location.location_id)}
                        data-test={`location-picker-item-${location.location_id}`}
                      >
                        <ListItemText
                          primary={location.location_name || `Location ${location.location_id}`}
                          secondary={
                            [`ID ${location.location_id}`, formatLocationAddress(location)]
                              .filter(Boolean)
                              .join(' · ') || undefined
                          }
                          secondaryTypographyProps={{
                            sx: { whiteSpace: 'normal', wordBreak: 'break-word' },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                  {hasMoreLocations && (
                    <Button
                      fullWidth
                      onClick={() => setPageSize((prev) => prev + PAGE_SIZE)}
                      data-test="machine-location-load-more"
                    >
                      Load more
                    </Button>
                  )}
                </>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {stage === 'pick' ? (
          <>
            <Button onClick={hide} data-test="machine-location-cancel">
              Cancel
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {hasCurrentLocation && (
                <Button
                  color="error"
                  onClick={goToConfirmRemove}
                  data-test="machine-location-remove"
                >
                  Remove from Location
                </Button>
              )}
              <Button
                variant="contained"
                disabled={!selectedLocation}
                onClick={goToConfirmMove}
                data-test="machine-location-continue"
              >
                Continue
              </Button>
            </Box>
          </>
        ) : stage === 'confirm-remove' ? (
          <>
            <Button
              onClick={() => setStage('pick')}
              disabled={isBusy}
              data-test="machine-location-back"
            >
              Back
            </Button>
            <LoadingButton
              color="error"
              variant="contained"
              onClick={handleConfirmRemove}
              loading={isRemoving}
              data-test="machine-location-confirm-remove"
            >
              Remove from Location
            </LoadingButton>
          </>
        ) : (
          <>
            <Button
              onClick={() => setStage('pick')}
              disabled={isBusy}
              data-test="machine-location-back"
            >
              Back
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleConfirmMove}
              loading={isBusy}
              data-test="machine-location-confirm-move"
            >
              {hasCurrentLocation ? 'Confirm Move' : 'Confirm Install'}
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </>
  );
};

export default ChangeMachineLocation;
